import * as React from "react";

import { type ProposalExecuteOptions, useProposalExecution } from "./useProposalExecution";

/**
 * useConfirmWithUndo — Gmail-style "Annulla invio" for the HITL confirm flow
 * (Feature 2). This is the SAFEST possible undo: it defers the ACTUAL execute
 * mutation by a short client-side window. If the user cancels within the
 * window the execute call NEVER fires — so it covers EVERY irreversible action
 * (email / message / SDI / SEPA) with zero backend complexity, because nothing
 * was ever sent.
 *
 * Lifecycle:
 *   confirmWithUndo() → ``pending`` (countdown) → window elapses → the shared
 *   ``useProposalExecution.execute`` runs exactly as a normal Confirm would
 *   (optimistic ``executing`` → authoritative ``executed | error`` patch,
 *   receipt + inline-recovery included). ``cancel()`` (or Esc, or the "Annulla"
 *   button) clears the timers and drops ``pending`` — no network call.
 *
 * ── Cancel-on-unmount (DOCUMENTED SAFE DEFAULT) ───────────────────────────
 * If the surface unmounts while a send is pending (the user navigates away
 * mid-window), we CANCEL — the execute never fires. For irreversible actions
 * "silently sent something as you left" is the dangerous outcome; "it didn't
 * send, re-confirm it" is the recoverable one. So we pick the recoverable
 * default and surface it via ``onCancel`` (the caller shows a toast). The
 * pending state is intentionally NOT persisted across reloads for the same
 * reason: an in-flight irreversible send must never resume unattended.
 */

/** Default pre-execute undo window. Tunable single source of truth. */
export const UNDO_WINDOW_MS = 6000;

export interface PendingUndoState {
  /** Whole seconds remaining before the deferred execute fires. */
  readonly secondsLeft: number;
  /** Total window length in seconds (drives the progress line). */
  readonly totalSeconds: number;
}

export interface ConfirmWithUndoOptions extends ProposalExecuteOptions {
  /**
   * Called when the pending send is cancelled — by the user (Annulla / Esc) OR
   * by cancel-on-unmount. Lets the caller show a "Invio annullato" toast. NOT
   * called once the window elapses and the execute actually fires.
   */
  readonly onUndo?: () => void;
  /**
   * Opt OUT of the delay and execute immediately. No current action requires
   * this (the whole point is that ALL sends are undoable), but the seam exists
   * for a future risk/undo flag that demands an immediate commit.
   */
  readonly immediate?: boolean;
}

export interface UseConfirmWithUndoResult {
  /** Arm the undo window then defer the real execute (or fire it immediately). */
  readonly confirmWithUndo: (
    proposalId: string,
    modifications: Readonly<Record<string, unknown>> | undefined,
    opts?: ConfirmWithUndoOptions,
  ) => void;
  /** Cancel a pending send (no-op if none). Fires ``onUndo``. */
  readonly cancel: () => void;
  /** Non-null while the undo window is counting down. */
  readonly pending: PendingUndoState | null;
  /** ``true`` while the deferred execute mutation is actually in flight. */
  readonly busy: boolean;
}

export function useConfirmWithUndo(): UseConfirmWithUndoResult {
  const { execute, busy } = useProposalExecution();
  const [pending, setPending] = React.useState<PendingUndoState | null>(null);

  // Timers + the "what to run when the window elapses" closure live in refs so
  // the tick/timeout callbacks stay stable and cleanup is race-free.
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const onUndoRef = React.useRef<(() => void) | undefined>(undefined);

  const clearTimers = React.useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const cancel = React.useCallback(() => {
    if (timeoutRef.current === null && intervalRef.current === null) return;
    clearTimers();
    setPending(null);
    const cb = onUndoRef.current;
    onUndoRef.current = undefined;
    cb?.();
  }, [clearTimers]);

  // Cancel-on-unmount (SAFE DEFAULT — see docstring). We deliberately do NOT
  // fire the pending execute here: an unattended irreversible send is the
  // outcome we're protecting against.
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current !== null || intervalRef.current !== null) {
        clearTimers();
        onUndoRef.current?.();
        onUndoRef.current = undefined;
      }
    };
  }, [clearTimers]);

  const confirmWithUndo = React.useCallback(
    (
      proposalId: string,
      modifications: Readonly<Record<string, unknown>> | undefined,
      opts: ConfirmWithUndoOptions = {},
    ) => {
      const { onUndo, immediate, ...executeOpts } = opts;
      if (immediate) {
        execute(proposalId, modifications, executeOpts);
        return;
      }
      // Re-arming replaces any prior pending window cleanly.
      clearTimers();
      onUndoRef.current = onUndo;
      const totalSeconds = Math.round(UNDO_WINDOW_MS / 1000);
      const startedAt = Date.now();
      setPending({ secondsLeft: totalSeconds, totalSeconds });

      intervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startedAt;
        const left = Math.max(0, Math.ceil((UNDO_WINDOW_MS - elapsed) / 1000));
        setPending((prev) => (prev ? { ...prev, secondsLeft: left } : prev));
      }, 250);

      timeoutRef.current = setTimeout(() => {
        clearTimers();
        onUndoRef.current = undefined;
        setPending(null);
        // The window elapsed untouched → commit exactly like a normal Confirm.
        execute(proposalId, modifications, executeOpts);
      }, UNDO_WINDOW_MS);
    },
    [execute, clearTimers],
  );

  return { confirmWithUndo, cancel, pending, busy };
}
